import { Book, BookStatus } from '../types/Book';
import { getStoredToken } from './authService';
import { API_BASE_URL } from '../config/api';

// Helper function to get auth token
const getAuthToken = async (): Promise<string> => {
  const token = getStoredToken();
  if (!token) {
    throw new Error('User not authenticated');
  }
  return token;
};

// Helper function to make authenticated API calls
const apiCall = async (endpoint: string, options: RequestInit = {}): Promise<any> => {
  try {
    const token = await getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Network error' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`API call failed for ${endpoint}:`, error);
    throw error;
  }
};

// Convert backend book data to Book interface
const backendBookToBook = (backendBook: any): Book => {
  // console.log('Converting backend book:', backendBook);
  
  if (!backendBook) {
    throw new Error('Backend book data is null or undefined');
  }
  
  if (!backendBook.book_id && !backendBook.id) {
    throw new Error('Backend book missing required ID field');
  }
  
  // Handle missing or empty titles gracefully
  const title = backendBook.title?.trim() || 'Untitled Book';
  
  const book: Book = {
    id: backendBook.book_id || backendBook.id,
    title: title,
    author: backendBook.author || '',
    isbn: backendBook.isbn,
    genre: backendBook.subjects && backendBook.subjects.length > 0 ? backendBook.subjects[0] : '',
    status: BookStatus.WANT_TO_READ, // Default status, could be enhanced later
    rating: backendBook.rating,
    dateAdded: backendBook.created_at || new Date().toISOString(),
    dateRead: backendBook.date_read,
    publicationDate: backendBook.publication_date,
    notes: backendBook.description,
    coverUrl: backendBook.cover_url,
    pageCount: backendBook.page_count,
    tags: backendBook.subjects || [],
    position_slot: backendBook.position_slot,
    // Reading progress fields
    currentPage: backendBook.current_page,
    readingProgress: backendBook.progress_percentage,
    totalPages: backendBook.total_pages || backendBook.page_count,
    readingStatus: backendBook.status,
    // Favorites functionality
    isFavorite: backendBook.is_favorite === 1 || backendBook.is_favorite === true || backendBook.isFavorite === true,
    favoriteNotes: backendBook.favorite_notes || backendBook.favoriteNotes,
    favoritedAt: backendBook.favorited_at || backendBook.favoritedAt,
    // Properties for metadata (e.g., linkedNoteId)
    properties: backendBook.properties || {}
  };
  
  // console.log('Converted book result:', book);
  return book;
};

// Cache helper functions
const CACHE_KEYS = {
  SUBJECTS: 'memory_land_subjects'
};

const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes in milliseconds

const cacheHelper = {
  set(key: string, data: any, sortBy: string) {
    try {
      const cacheData = {
        data,
        timestamp: Date.now(),
        sortBy
      };
      sessionStorage.setItem(key, JSON.stringify(cacheData));
      console.log(`Cache set for key: ${key} with sortBy: ${sortBy}`);
    } catch (error) {
      console.warn('Failed to set cache:', error);
    }
  },

  get(key: string, sortBy: string): any | null {
    try {
      const cached = sessionStorage.getItem(key);
      if (!cached) return null;
      
      const cacheData = JSON.parse(cached);
      const isExpired = Date.now() - cacheData.timestamp > CACHE_DURATION;
      const sortByChanged = cacheData.sortBy !== sortBy;
      
      if (isExpired || sortByChanged) {
        sessionStorage.removeItem(key);
        console.log(`Cache expired or sortBy changed for key: ${key}. Expired: ${isExpired}, SortBy changed: ${sortByChanged}`);
        return null;
      }
      
      console.log(`Cache hit for key: ${key} with sortBy: ${sortBy}`);
      return cacheData.data;
    } catch (error) {
      console.warn('Failed to get cache:', error);
      sessionStorage.removeItem(key);
      return null;
    }
  },

  clear(key: string) {
    try {
      sessionStorage.removeItem(key);
      console.log(`Cache cleared for key: ${key}`);
    } catch (error) {
      console.warn('Failed to clear cache:', error);
    }
  }
};

// Book service functions
export const bookService = {
  // Get all books
  async getBooks(): Promise<Book[]> {
    try {
      console.log('Fetching all books from Express.js backend');
      
      // Use search endpoint with no filters to get all books
      const result = await apiCall('/books/search?limit=50&offset=0');
      
      console.log('Search books result:', result);
      
      if (result.success && result.data && Array.isArray(result.data.books)) {
        console.log('Backend books returned:', result.data.books.length, 'books');
        
        // Convert backend books to Book interface with better error handling
        const books: Book[] = [];
        
        for (let i = 0; i < result.data.books.length; i++) {
          const backendBook = result.data.books[i];
          try {
            const convertedBook = backendBookToBook(backendBook);
            books.push(convertedBook);
          } catch (error) {
            console.error(`Error converting book at index ${i}:`, backendBook, error);
            // Skip this book instead of adding a placeholder
            continue;
          }
        }
        
        console.log('Successfully converted books:', books.length);
        return books;
      }
      
      console.log('Search books returned invalid data structure or success: false');
      return [];
    } catch (error) {
      console.error('Error fetching books:', error);
      return [];
    }
  },

  // Add a new book
  async addBook(book: Omit<Book, 'id'>): Promise<Book | null> {
    try {
      // Generate a unique book_id
      const bookId = `book_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Prepare subjects array, including genre if it's not already in tags
      const subjects = [...(book.tags || [])];
      if (book.genre && !subjects.includes(book.genre)) {
        subjects.push(book.genre);
      }

      const backendBookData = {
        book_id: bookId,
        title: book.title,
        author: book.author,
        isbn: book.isbn,
        publication_date: book.dateRead ? new Date(book.dateRead).getFullYear().toString() : undefined,
        language: 'en', // Default to English
        description: book.notes,
        publisher: undefined,
        physical_description: book.pageCount ? `${book.pageCount} pages` : undefined,
        subjects: subjects,
        urls: []
      };

      const result = await apiCall('/books', {
        method: 'POST',
        body: JSON.stringify(backendBookData)
      });
      
      if (result.success && result.data) {
        const newBook = {
          ...book,
          id: result.data.book_id || result.data.bookId
        } as Book;
        
        return newBook;
      }
      
      return null;
    } catch (error) {
      console.error('Error adding book:', error);
      return null;
    }
  },

  // Update a book
  async updateBook(book: Book): Promise<boolean> {
    try {
      // Prepare subjects array, including genre if it's not already in tags
      const subjects = [...(book.tags || [])];
      if (book.genre && !subjects.includes(book.genre)) {
        subjects.push(book.genre);
      }

      const backendBookData: any = {
        book_id: book.id,
        title: book.title,
        author: book.author,
        isbn: book.isbn,
        publication_date: book.dateRead ? new Date(book.dateRead).getFullYear().toString() : undefined,
        language: 'en',
        description: book.notes,
        publisher: undefined,
        physical_description: book.pageCount ? `${book.pageCount} pages` : undefined,
        subjects: subjects,
        urls: []
      };

      // Include properties if they exist
      if (book.properties) {
        backendBookData.properties = book.properties;
      }

      const result = await apiCall(`/books/${encodeURIComponent(book.id)}`, {
        method: 'PUT',
        body: JSON.stringify(backendBookData)
      });
      
      return result.success;
    } catch (error) {
      console.error('Error updating book:', error);
      return false;
    }
  },

  // Delete a book
  async deleteBook(bookId: string): Promise<boolean> {
    try {
      const result = await apiCall(`/books/${encodeURIComponent(bookId)}`, {
        method: 'DELETE'
      });
      
      return result.success;
    } catch (error) {
      console.error('Error deleting book:', error);
      return false;
    }
  },

  // Search books with enhanced filtering
  async searchBooks(query: string = '', filters?: { 
    author?: string; 
    language?: string; 
    subject?: string; 
    publication_year?: string;
    status?: BookStatus;
    minRating?: number;
  }): Promise<Book[]> {
    try {
      const searchParams = new URLSearchParams();
      
      if (query) searchParams.append('query', query);
      if (filters?.author) searchParams.append('author', filters.author);
      if (filters?.language) searchParams.append('language', filters.language);
      if (filters?.subject) searchParams.append('subject', filters.subject);
      if (filters?.publication_year) searchParams.append('publication_year', filters.publication_year);
      
      searchParams.append('limit', '50');
      searchParams.append('offset', '0');

      console.log('Searching books with params:', searchParams.toString());

      const result = await apiCall(`/books/search?${searchParams.toString()}`);
      
      if (result.success && result.data && result.data.books) {
        let books = result.data.books.map((book: any) => backendBookToBook(book));
        
        // Apply client-side filters that aren't supported by backend
        if (filters?.status) {
          books = books.filter((book: Book) => book.status === filters.status);
        }
        
        if (filters?.minRating) {
          books = books.filter((book: Book) => book.rating && book.rating >= filters.minRating!);
        }
        
        return books;
      }
      
      return [];
    } catch (error) {
      console.error('Error searching books:', error);
      return [];
    }
  },

  // Enhanced search with all filters (replaces the need for separate getBooks)
  async searchBooksWithFilters(searchQuery: string, filters: {
    status?: BookStatus;
    author?: string;
    subject?: string;
    language?: string;
    publication_year?: string;
    minRating?: number;
  }): Promise<Book[]> {
    return this.searchBooks(searchQuery, filters);
  },

  // Get books by subject/genre
  async getBooksBySubject(subject: string): Promise<Book[]> {
    try {
      const result = await apiCall(`/books/subjects/${encodeURIComponent(subject)}?limit=50&offset=0`);
      
      if (result.success && result.data && result.data.books) {
        return result.data.books.map(backendBookToBook);
      }
      
      return [];
    } catch (error) {
      console.error('Error getting books by subject:', error);
      return [];
    }
  },

  // Get popular books
  async getPopularBooks(): Promise<Book[]> {
    try {
      const result = await apiCall('/books/popular?limit=20');
      
      if (result.success && result.data && result.data.books) {
        return result.data.books.map(backendBookToBook);
      }
      
      return [];
    } catch (error) {
      console.error('Error getting popular books:', error);
      return [];
    }
  },

  // Get recent books
  async getRecentBooks(): Promise<Book[]> {
    try {
      console.log('Fetching recent books...');
      const result = await apiCall('/books/recent?limit=20');
      
      console.log('getRecentBooks result:', result);
      
      if (result && result.success && result.data && Array.isArray(result.data.books)) {
        console.log('Raw recent books:', result.data.books.length, 'books');
        
        // Convert books with individual error handling
        const books: Book[] = [];
        
        for (let i = 0; i < result.data.books.length; i++) {
          const backendBook = result.data.books[i];
          try {
            const convertedBook = backendBookToBook(backendBook);
            books.push(convertedBook);
          } catch (error) {
            console.error(`Error converting recent book at index ${i}:`, backendBook, error);
            // Skip this book and continue with others
            continue;
          }
        }
        
        console.log('Successfully converted recent books:', books.length);
        return books;
      }
      
      console.log('getRecentBooks returned invalid data or success: false');
      return [];
    } catch (error) {
      console.error('Error getting recent books:', error);
      return [];
    }
  },

  // Get all subjects (for filtering) with pagination and caching - returns both data and pagination info
  async getSubjects(limit: number = 50, offset: number = 0, orderBy: 'book_count' | 'subject_name' = 'book_count'): Promise<{
    subjects: { subject_name: string; book_count: number }[];
    pagination: {
      limit: number;
      offset: number;
      total: number;
      hasMore: boolean;
      nextOffset: number | null;
    } | null;
  }> {
    try {
      console.log(`Fetching subjects from Express.js backend (limit: ${limit}, offset: ${offset}, orderBy: ${orderBy})...`);
      
      // Check cache only for first page (offset 0) to keep pagination logic simple
      if (offset === 0) {
        const cached = cacheHelper.get(CACHE_KEYS.SUBJECTS, orderBy);
        if (cached) {
          console.log('Using cached subjects data');
          return cached;
        }
      }
      
      const searchParams = new URLSearchParams();
      searchParams.append('limit', limit.toString());
      searchParams.append('offset', offset.toString());
      searchParams.append('orderBy', orderBy);
      
      const result = await apiCall(`/books/subjects?${searchParams.toString()}`);
      
      console.log('getSubjects raw result:', result);
      
      if (!result || !result.success || !result.data) {
        console.warn('getSubjects returned null data or success: false - backend may be unavailable');
        return { subjects: [], pagination: null };
      }
      
      if (result.data.subjects && Array.isArray(result.data.subjects)) {
        console.log('Raw subjects data:', result.data.subjects);
        console.log('Pagination info:', result.data.pagination);
        
        if (result.data.pagination) {
          console.log(`Fetched ${result.data.subjects.length} subjects (${result.data.pagination.offset + 1}-${result.data.pagination.offset + result.data.subjects.length} of ${result.data.pagination.total})`);
        } else {
          console.log(`Fetched ${result.data.subjects.length} subjects (pagination info not available)`);
        }
        
        const response = {
          subjects: result.data.subjects,
          pagination: result.data.pagination || null
        };
        
        // Cache only the first page for better performance
        if (offset === 0) {
          cacheHelper.set(CACHE_KEYS.SUBJECTS, response, orderBy);
        }
        
        return response;
      }
      
      console.warn('getSubjects returned success: true but no subjects array');
      console.warn('Full result data:', result);
      return { subjects: [], pagination: null };
    } catch (error) {
      console.error('Error getting subjects:', error);
      // Provide more detailed error information for debugging
      if (error instanceof Error) {
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
      }
      return { subjects: [], pagination: null };
    }
  },

  // Get subject names only (for backward compatibility)
  async getSubjectNames(limit: number = 50, offset: number = 0, orderBy: 'book_count' | 'subject_name' = 'book_count'): Promise<string[]> {
    const result = await this.getSubjects(limit, offset, orderBy);
    return result.subjects.map(subject => subject.subject_name);
  },

  // Get a specific book by ID
  async getBookById(bookId: string): Promise<Book | null> {
    try {
      const result = await apiCall(`/books/${encodeURIComponent(bookId)}`);
      
      if (result.success && result.data && result.data.book) {
        return backendBookToBook(result.data.book);
      }
      
      return null;
    } catch (error) {
      console.error('Error getting book by ID:', error);
      return null;
    }
  },

  // ============================================
  // FAVORITES FUNCTIONALITY
  // ============================================

  // Add book to favorites
  async addToFavorites(bookId: string, notes?: string): Promise<boolean> {
    try {
      const result = await apiCall(`/books/${encodeURIComponent(bookId)}/favorite`, {
        method: 'POST',
        body: JSON.stringify({ notes })
      });
      
      if (result.success) {
        console.log('Book added to favorites successfully');
        return true;
      }
      
      console.error('Failed to add book to favorites:', result.message);
      return false;
    } catch (error) {
      console.error('Error adding book to favorites:', error);
      return false;
    }
  },

  // Remove book from favorites
  async removeFromFavorites(bookId: string): Promise<boolean> {
    try {
      const result = await apiCall(`/books/${encodeURIComponent(bookId)}/favorite`, {
        method: 'DELETE'
      });
      
      if (result.success) {
        console.log('Book removed from favorites successfully');
        return true;
      }
      
      console.error('Failed to remove book from favorites:', result.message);
      return false;
    } catch (error) {
      console.error('Error removing book from favorites:', error);
      return false;
    }
  },

  // Get user's favorite books
  async getUserFavorites(limit: number = 50, offset: number = 0): Promise<Book[]> {
    try {
      console.log('Fetching user favorites...');
      const result = await apiCall(`/books/user/favorites?limit=${limit}&offset=${offset}`);
      
      console.log('getUserFavorites result:', result);
      
      if (result && result.success && result.data && Array.isArray(result.data.favorites)) {
        console.log('Raw favorites data:', result.data.favorites.length, 'books');
        
        // Convert favorites with individual error handling
        const books: Book[] = [];
        
        for (let i = 0; i < result.data.favorites.length; i++) {
          const favorite = result.data.favorites[i];
          try {
            // The favorite object contains both book data and favorite metadata
            const convertedBook = backendBookToBook(favorite);
            // Add favorite-specific data
            convertedBook.favoriteNotes = favorite.notes;
            convertedBook.favoritedAt = favorite.favorited_at;
            convertedBook.isFavorite = true;
            books.push(convertedBook);
          } catch (error) {
            console.error(`Error converting favorite book at index ${i}:`, favorite, error);
            // Skip this book and continue with others
            continue;
          }
        }
        
        console.log('Successfully converted favorite books:', books.length);
        return books;
      }
      
      console.log('getUserFavorites returned invalid data or success: false');
      return [];
    } catch (error) {
      console.error('Error getting user favorites:', error);
      return [];
    }
  },

  // ============================================
  // READING PROGRESS FUNCTIONALITY
  // ============================================

  // Update reading progress for a book
  async updateReadingProgress(bookId: string, progress: {
    currentPage?: number;
    totalPages?: number;
    status?: string;
  }): Promise<boolean> {
    try {
      const progressData: any = {};
      
      if (progress.currentPage !== undefined) {
        progressData.current_page = progress.currentPage;
      }
      if (progress.totalPages !== undefined) {
        progressData.total_pages = progress.totalPages;
      }
      if (progress.status !== undefined) {
        progressData.status = progress.status;
      }

      const result = await apiCall(`/books/${encodeURIComponent(bookId)}/progress`, {
        method: 'POST',
        body: JSON.stringify(progressData)
      });

      return result.success || false;
    } catch (error) {
      console.error('Error updating reading progress:', error);
      return false;
    }
  },

  // Get books with reading progress for the current user
  async getBooksWithProgress(limit: number = 50, offset: number = 0): Promise<any> {
    try {
      console.log('Fetching books with reading progress...');
      
      const searchParams = new URLSearchParams();
      searchParams.append('limit', limit.toString());
      searchParams.append('offset', offset.toString());

      const result = await apiCall(`/books/user/progress?${searchParams.toString()}`);

      console.log('getBooksWithProgress result:', result);

      if (result && result.success && result.data && Array.isArray(result.data.books)) {
        console.log('Raw books with progress data:', result.data.books.length, 'books');

        // Convert books with individual error handling
        const books: Book[] = [];

        for (let i = 0; i < result.data.books.length; i++) {
          const backendBook = result.data.books[i];
          try {
            const convertedBook = backendBookToBook(backendBook);
            books.push(convertedBook);
          } catch (error) {
            console.error(`Error converting book with progress at index ${i}:`, backendBook, error);
            // Skip this book and continue with others
            continue;
          }
        }

        console.log('Successfully converted books with progress:', books.length);
        return {
          ...result.data,
          books: books
        };
      }

      console.log('getBooksWithProgress returned invalid data or success: false');
      return { books: [], pagination: null };
    } catch (error) {
      console.error('Error getting books with progress:', error);
      return { books: [], pagination: null };
    }
  },

  // ============================================
  // NOTE LINKING FUNCTIONALITY
  // ============================================

  // Get linked note ID for a book
  async getBookNoteId(bookId: string): Promise<string | null> {
    try {
      const book = await this.getBookById(bookId);
      if (book && book.properties && book.properties.linkedNoteId) {
        return book.properties.linkedNoteId;
      }
      return null;
    } catch (error) {
      console.error('Error getting book note ID:', error);
      return null;
    }
  },

  // Create a note for a book (lazy creation)
  async createBookNote(bookId: string, userId: number, bookTitle: string): Promise<string> {
    try {
      // Import NotesService dynamically to avoid circular dependencies
      const { NotesService } = await import('./notesService');
      
      // Sanitize book title for note_id
      const sanitizedTitle = bookTitle.toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      const noteId = `book-${bookId}-${sanitizedTitle}`;

      // First, try to get the note if it already exists
      try {
        const existingNote = await NotesService.getNoteById(noteId, userId);
        // Note exists, update book properties if needed and return the note ID
        const book = await this.getBookById(bookId);
        if (book && (!book.properties || !book.properties.linkedNoteId)) {
          const currentProperties = book.properties || {};
          const updatedProperties = {
            ...currentProperties,
            linkedNoteId: existingNote.note_id
          };
          await this.updateBook({
            ...book,
            properties: updatedProperties
          });
        }
        return existingNote.note_id;
      } catch (getError: any) {
        // Note doesn't exist yet, continue to create it
        if (getError.status !== 404 && !getError.message?.includes('not found')) {
          // If it's not a "not found" error, rethrow it
          throw getError;
        }
      }

      // Create note with type 'book' and link to book
      try {
        const newNote = await NotesService.createNoteWithId(
          userId,
          noteId,
          `${bookTitle} - Reading Notes`,
          [],
          {
            linkedBookId: bookId,
            type: 'book'
          }
        );

        // Update book properties with linked note_id
        const book = await this.getBookById(bookId);
        if (book) {
          const currentProperties = book.properties || {};
          const updatedProperties = {
            ...currentProperties,
            linkedNoteId: newNote.note_id
          };

          // Update book with new properties
          // Note: This assumes the backend supports updating properties
          // If not, we may need a separate endpoint
          await this.updateBook({
            ...book,
            properties: updatedProperties
          });
        }

        return newNote.note_id;
      } catch (createError: any) {
        // If creation fails due to duplicate entry, try to get the existing note
        if (createError.message?.includes('already exists') || createError.error === 'Validation error') {
          try {
            const existingNote = await NotesService.getNoteById(noteId, userId);
            // Update book properties if needed
            const book = await this.getBookById(bookId);
            if (book && (!book.properties || !book.properties.linkedNoteId)) {
              const currentProperties = book.properties || {};
              const updatedProperties = {
                ...currentProperties,
                linkedNoteId: existingNote.note_id
              };
              await this.updateBook({
                ...book,
                properties: updatedProperties
              });
            }
            return existingNote.note_id;
          } catch (getError) {
            // If we still can't get it, throw the original create error
            throw createError;
          }
        }
        // For other errors, rethrow
        throw createError;
      }
    } catch (error) {
      console.error('Error creating book note:', error);
      throw error;
    }
  },

  // ============================================
  // READING RECORDS FUNCTIONALITY
  // ============================================

  // Create a reading record
  async createReadingRecord(bookId: string, recordData: {
    record_date: string; // YYYY-MM-DD
    pages_read?: number;
    start_page?: number;
    end_page?: number;
    reading_time_minutes?: number;
    notes?: string;
  }): Promise<any> {
    try {
      const result = await apiCall(`/books/${encodeURIComponent(bookId)}/records`, {
        method: 'POST',
        body: JSON.stringify(recordData)
      });

      if (result.success && result.data && result.data.record) {
        return result.data.record;
      }

      return null;
    } catch (error) {
      console.error('Error creating reading record:', error);
      throw error;
    }
  },

  // Get reading records for a book
  async getReadingRecords(bookId: string, options?: {
    limit?: number;
    offset?: number;
    orderBy?: 'record_date' | 'created_at';
  }): Promise<any[]> {
    try {
      const params = new URLSearchParams();
      if (options?.limit) params.append('limit', options.limit.toString());
      if (options?.offset) params.append('offset', options.offset.toString());
      if (options?.orderBy) params.append('orderBy', options.orderBy);

      const queryString = params.toString();
      const endpoint = `/books/${encodeURIComponent(bookId)}/records${queryString ? `?${queryString}` : ''}`;
      
      const result = await apiCall(endpoint);

      if (result.success && result.data && Array.isArray(result.data.records)) {
        return result.data.records;
      }

      return [];
    } catch (error) {
      console.error('Error getting reading records:', error);
      return [];
    }
  },

  // Get reading records grouped by date
  async getReadingRecordsByDate(bookId: string, startDate?: string, endDate?: string): Promise<any[]> {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const queryString = params.toString();
      const endpoint = `/books/${encodeURIComponent(bookId)}/records/by-date${queryString ? `?${queryString}` : ''}`;
      
      const result = await apiCall(endpoint);

      if (result.success && result.data && Array.isArray(result.data.records)) {
        return result.data.records;
      }

      return [];
    } catch (error) {
      console.error('Error getting reading records by date:', error);
      return [];
    }
  },

  // Delete a reading record
  async deleteReadingRecord(bookId: string, recordId: number): Promise<boolean> {
    try {
      const result = await apiCall(`/books/${encodeURIComponent(bookId)}/records/${recordId}`, {
        method: 'DELETE'
      });

      return result.success || false;
    } catch (error) {
      console.error('Error deleting reading record:', error);
      return false;
    }
  }
};